import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BaseCall {
  memberCIDName: string;
  memberCIDNumber: string;
  joinedEpoch: number;
  queueName: string;
  agentName: string;
}

export interface ActiveCall extends BaseCall {
  calledTime: number;
  memberSessionUUID: string;
  name: string;
}

export interface QueueCall extends BaseCall {
  sessionUUID: string;
  queue: string;
}

export interface AgentOffering {
  uuid: string;
  sessionUUID: string;
  queue: string;
  callerIDName: string;
  callerIDNumber: string;
  event: string;
  domain: string;
  agent: string;
}

interface CallCenterState {
  activeCalls: Record<string, ActiveCall>;
  queueCalls: Record<string, QueueCall>;
  agentsOffering: Record<string, AgentOffering>;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  selectedDepartmentIds: string[];
}

const initialState: CallCenterState = {
  activeCalls: {},
  queueCalls: {},
  agentsOffering: {},
  status: "idle",
  error: null,
  selectedDepartmentIds: [],
};

const callCenterSlice = createSlice({
  name: "callCenter",
  initialState,
  reducers: {
    setActiveCalls: (state, action: PayloadAction<ActiveCall[]>) => {
      state.activeCalls = {};
      action.payload.forEach(call => {
        state.activeCalls[call.memberSessionUUID] = call;
      });
      state.status = "succeeded";
      state.error = null;
    },

    setQueueCalls: (state, action: PayloadAction<QueueCall[]>) => {
      state.queueCalls = {};
      action.payload.forEach(call => {
        state.queueCalls[call.sessionUUID] = call;
      });
      state.status = "succeeded";
      state.error = null;
    },

    setAgentsOffering: (state, action: PayloadAction<AgentOffering[]>) => {
      state.agentsOffering = {};
      action.payload.forEach(offering => {
        const cleanAgentId = offering.agent.split('@')[0];
        state.agentsOffering[cleanAgentId] = {
          ...offering,
          agent: cleanAgentId
        };
      });
      state.status = "succeeded";
      state.error = null;
    },

    addActiveCall: (state, action: PayloadAction<ActiveCall | ActiveCall[]>) => {
      if (Array.isArray(action.payload)) {
        state.activeCalls = {};
        (action.payload as ActiveCall[]).forEach((call) => {
          call.agentName = "";
          state.activeCalls[call.memberSessionUUID] = call;
        });
        return;
      }
      const payloadCall = action.payload as ActiveCall;
      const callIndex = Object.values(state.activeCalls).findIndex(
        (call) => call.memberSessionUUID === payloadCall.memberSessionUUID
      );
      payloadCall.agentName = "";
      if (callIndex !== -1) {
        const key = Object.keys(state.activeCalls)[callIndex];
        state.activeCalls[key] = payloadCall;
      } else {
        state.activeCalls[payloadCall.memberSessionUUID] = payloadCall;
      }
    },

    addQueueCall: (state, action: PayloadAction<QueueCall>) => {
      state.queueCalls[action.payload.sessionUUID] = action.payload;
    },

    addAgentOffering: (state, action: PayloadAction<AgentOffering>) => {
      const cleanAgentId = action.payload.agent.split('@')[0];
      state.agentsOffering[cleanAgentId] = {
        ...action.payload,
        agent: cleanAgentId
      };
    },

    addAgentOfferings: (state, action: PayloadAction<AgentOffering | AgentOffering[]>) => {
      if (Array.isArray(action.payload)) {
        action.payload.forEach(offering => {
          const cleanAgentId = offering.agent.split('@')[0];
          state.agentsOffering[cleanAgentId] = {
            ...offering,
            agent: cleanAgentId
          };
        });
      } else {
        const cleanAgentId = action.payload.agent.split('@')[0];
        state.agentsOffering[cleanAgentId] = {
          ...action.payload,
          agent: cleanAgentId
        };
      }
    },

    removeActiveCall: (state, action: PayloadAction<string>) => {
      delete state.activeCalls[action.payload];
    },

    removeQueueCall: (state, action: PayloadAction<string>) => {
      delete state.queueCalls[action.payload];
    },

    removeAgentOfferingBySessionUUID: (state, action: PayloadAction<string>) => {
      const index = Object.values(state.agentsOffering).findIndex(
        (offering) => offering.sessionUUID === action.payload
      );
      if (index !== -1) {
        const key = Object.keys(state.agentsOffering)[index];
        delete state.agentsOffering[key];
      }
    },

    removeQueueCallBySessionUUID: (state, action: PayloadAction<string>) => {
      const index = Object.values(state.queueCalls).findIndex(
        (call) => call.sessionUUID === action.payload
      );
      if (index !== -1) {
        const key = Object.keys(state.queueCalls)[index];
        delete state.queueCalls[key];
      }
    },

    removeActiveCallBySessionUUID: (state, action: PayloadAction<string>) => {
      const index = Object.values(state.activeCalls).findIndex(
        (call) => call.memberSessionUUID === action.payload
      );
      if (index !== -1) {
        const key = Object.keys(state.activeCalls)[index];
        delete state.activeCalls[key];
      }
    },

    removeAgentOfferingByAgent: (state, action: PayloadAction<string>) => {
      const cleanAgentId = typeof action.payload === "string" ? action.payload.split('@')[0] : "";
      if (cleanAgentId) {
        delete state.agentsOffering[cleanAgentId];
      }
    },

    updateCallCenterData: (
      state,
      action: PayloadAction<{
        activeCalls: ActiveCall[];
        queueCalls: QueueCall[];
        agentsOffering: AgentOffering[];
      }>
    ) => {
      state.activeCalls = {};
      state.queueCalls = {};
      state.agentsOffering = {};
      
      action.payload.activeCalls.forEach(call => {
        state.activeCalls[call.memberSessionUUID] = call;
      });
      
      action.payload.queueCalls.forEach(call => {
        state.queueCalls[call.sessionUUID] = call;
      });
      
      action.payload.agentsOffering.forEach(offering => {
        const cleanAgentId = offering.agent.split('@')[0];
        state.agentsOffering[cleanAgentId] = {
          ...offering,
          agent: cleanAgentId
        };
      });
      
      state.status = "succeeded";
      state.error = null;
    },

    setCallCenterLoading: (state) => {
      state.status = "loading";
      state.error = null;
    },

    setCallCenterError: (state, action: PayloadAction<string>) => {
      state.status = "failed";
      state.error = action.payload;
    },

    resetCallCenter: (state) => {
      state.activeCalls = {};
      state.queueCalls = {};
      state.agentsOffering = {};
      state.status = "idle";
      state.error = null;
    },

    setSelectedDepartmentIds: (state, action: PayloadAction<string[]>) => {
      state.selectedDepartmentIds = action.payload;
    },
  },
});

export const {
  setActiveCalls,
  setQueueCalls,
  setAgentsOffering,
  addActiveCall,
  addQueueCall,
  addAgentOffering,
  addAgentOfferings,
  removeActiveCall,
  removeQueueCall,
  removeAgentOfferingBySessionUUID,
  removeQueueCallBySessionUUID,
  removeActiveCallBySessionUUID,
  removeAgentOfferingByAgent,
  updateCallCenterData,
  setCallCenterLoading,
  setCallCenterError,
  resetCallCenter,
  setSelectedDepartmentIds,
} = callCenterSlice.actions;

export default callCenterSlice.reducer;
